import React, { useEffect, useState } from "react";
import { IoSchool, IoBook } from "react-icons/io5";
import { MdCorporateFare } from "react-icons/md";
import { FaArrowRight, FaCheck } from "react-icons/fa";
import collegeSmart from "../../assets/images/college-smart.png";
import schoolSmart from "../../assets/images/school-smart.png";
import corporateSmart from "../../assets/images/corporate-smart.png";
import { Button, Card, Container, IconTile, SectionHead } from "./ui";

const servicesCourses = {
  college: [
    "Campus Recruitment Training covering Quants, Reasoning, Verbal, Technical and Soft skills.",
    "Company Specific Training (Product Based and Service Based)",
    "Credit Course Training (Universities and Autonomous Colleges)",
    "Core Technical Training (C, C++, JAVA, PYTHON, R, PHP, etc.,)",
    "Expert Lectures on Futuristic Technologies (BIG DATA, AI, VR, AR, Data Analytics, Machine Learning, Deep Learning, etc.,)",
    "English Language Development Programmes (TOEFL & IELTS)",
    "GRE, IBPS & other Bank exams.",
  ],
  corporate: [
    "Hire, Train & Deploy",
    "Campus to Corporate Training",
    "Recruitment Assessment",
    "Fresher Induction Training",
  ],
  school: ["SMART English", "SMART Maths", "SMART Science", "SMART Coding"],
};

const serviceData = [
  {
    id: 1,
    title: "Colleges",
    icon: IoBook,
    description:
      "Empowering students with industry-relevant training, placement support, company specific, skill development programs, aptitude training, soft skills, career guidance competitive exam and preparation foreign language TOFL ILETS & GRE.",
    image: collegeSmart,
    courses: servicesCourses.college,
  },
  {
    id: 2,
    title: "Corporates",
    icon: MdCorporateFare,
    description:
      "Enhancing workforce productivity through corporate training, leadership development, technical upskilling, employee engagement programs, and customized learning solutions.",
    image: corporateSmart,
    courses: servicesCourses.corporate,
  },
  {
    id: 3,
    title: "Schools",
    icon: IoSchool,
    description:
      "Building strong foundations for future success through career awareness programs, communication skills training, aptitude development, life skills workshops, and competitive exam preparation foreign language TOFL ILETS & GRE.",
    image: schoolSmart,
    courses: servicesCourses.school,
  },
];

const whyChooseUs = [
  {
    icon: IoBook,
    title: "Comprehensive Curriculum",
    description:
      "Our programs cover all essential skills from technical knowledge to soft skills development.",
  },
  {
    icon: MdCorporateFare,
    title: "Industry-Relevant Training",
    description:
      "Stay updated with the latest industry trends and technologies through our expert-led programs.",
  },
  {
    icon: IoSchool,
    title: "Proven Track Record",
    description:
      "With 24+ years of experience and 4M+ students trained, we have a proven track record of success.",
  },
];

const ServicesComponent = ({ serviceComponentRef, activeKey, setActiveKey }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => entry.isIntersecting && setIsVisible(true),
      { threshold: 0.1 }
    );
    if (serviceComponentRef.current) {
      observer.observe(serviceComponentRef.current);
    }
    return () => observer.disconnect();
  }, []);

  const active = serviceData.find((service) => service.id === activeKey);

  return (
    <section
      ref={serviceComponentRef}
      id="services"
      className="scroll-mt-24 bg-white py-20"
    >
      <Container>
        <SectionHead
          eyebrow="Our Services"
          title="Services we offer"
          subtitle="Comprehensive training solutions tailored for colleges, corporates, and schools."
          action={
            <Button
              variant="outline"
              onClick={() =>
                window.open("https://app.smartica.co.in/en/", "_blank")
              }
            >
              All Programs
            </Button>
          }
        />

        {/* Category cards — the selected one fills with the accent */}
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {serviceData.map((service) => {
            const isActive = service.id === activeKey;
            return (
              <button key={service.id} onClick={() => setActiveKey(service.id)}>
                <Card
                  tone={isActive ? "accent" : "white"}
                  className={`flex h-full w-full items-center gap-4 p-5 text-left ${
                    isActive ? "shadow-md" : "hover:border-brand/40 hover:shadow-sm"
                  }`}
                >
                  <IconTile
                    icon={service.icon}
                    tone={isActive ? "white" : "tint"}
                  />
                  <div>
                    <h3
                      className={`text-base font-bold ${
                        isActive ? "text-white" : "text-ink"
                      }`}
                    >
                      {service.title}
                    </h3>
                    <p
                      className={`text-sm ${
                        isActive ? "text-white/80" : "text-darkgrey"
                      }`}
                    >
                      {service.courses.length} programmes available
                    </p>
                  </div>
                </Card>
              </button>
            );
          })}
        </div>

        {/* Detail panel for the selected category */}
        <div
          className={`mt-12 ${isVisible ? "animate-fade-in-up" : "opacity-0"}`}
        >
          <div className="grid items-center gap-10 lg:grid-cols-2">
            <div className="rounded-3xl bg-brand-50 p-8">
              <img
                src={active.image}
                alt={active.title}
                className="h-auto w-full object-contain"
              />
            </div>

            <div>
              <h3 className="text-2xl font-bold text-ink">{active.title}</h3>
              <p className="mt-3 leading-relaxed text-darkgrey">
                {active.description}
              </p>

              <h4 className="mb-4 mt-8 text-sm font-bold uppercase tracking-wider text-ink">
                What We Offer
              </h4>
              <ul className="space-y-3">
                {active.courses.map((course) => (
                  <li key={course} className="flex items-start gap-3">
                    <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-brand-100">
                      <FaCheck className="h-2.5 w-2.5 text-brand" />
                    </span>
                    <span className="text-sm leading-relaxed text-darkgrey">
                      {course}
                    </span>
                  </li>
                ))}
              </ul>

              <Button
                className="mt-8 group"
                onClick={() =>
                  window.open("https://app.smartica.co.in/en/", "_blank")
                }
              >
                Explore {active.title} Programs
                <FaArrowRight className="transition-transform duration-300 group-hover:translate-x-1" />
              </Button>
            </div>
          </div>
        </div>

        {/* Why choose us */}
        <div className="mt-20 rounded-3xl bg-brand-50 p-8 sm:p-12">
          <div className="mx-auto mb-10 max-w-2xl text-center">
            <h3 className="text-2xl font-bold text-ink sm:text-3xl">
              Why choose our services?
            </h3>
            <p className="mt-3 text-darkgrey">
              We combine industry expertise with innovative teaching methods to
              deliver exceptional results
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-3">
            {whyChooseUs.map((item) => (
              <Card key={item.title} className="p-6">
                <IconTile icon={item.icon} className="mb-4" />
                <h4 className="mb-2 text-base font-bold text-ink">
                  {item.title}
                </h4>
                <p className="text-sm leading-relaxed text-darkgrey">
                  {item.description}
                </p>
              </Card>
            ))}
          </div>
        </div>
      </Container>
    </section>
  );
};

export default ServicesComponent;
